import { t as localeText } from "@/shared/lib/translate";
import { useTranslation as useLocaleSubscription } from "react-i18next";
import { AUDIO_TOOLS, type AudioTool } from "@/shared/lib/audioTools";
import { IMAGE_TOOLS, imageStudioTool, type ImageTool } from "@/shared/lib/imageTools";
import { VIDEO_TOOLS, type VideoTool } from "@/shared/lib/videoTools";
import { CaretDownIcon } from "@phosphor-icons/react/dist/csr/CaretDown";
import { Link } from "@tanstack/react-router";
import { ImageIcon, VideoIcon as VideoCameraIcon, WaveformIcon } from "./icons";
import { type MouseEvent, type ReactNode, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/shared/lib/cn";
import { Button, buttonVariants } from "./Button";
import { ActionCard } from "./ActionCard";
import { CreationToolIcon } from "./CreationToolIcon";
import { CheckIcon, ThreeDIcon } from "./icons";
import { useCreationWorkspaceLayout, type WorkspaceCreationMedia } from "./CreationWorkspaceLayout";
import "./CreationStudio.css";

/** Image tools as quiet tabs above the creations; the sidebar owns the media. */
export function ImageToolTabs(props: { active: ImageTool; onChange: (tool: ImageTool) => void }) {
  useLocaleSubscription();
  return <CreationToolTabs {...props} label={localeText("interface.image_tools_9b470a67", { defaultValue: "Image tools" })} tools={IMAGE_TOOLS.map(tool => ({ ...tool, studio: imageStudioTool(tool.id) }))} />;
}

export function VideoToolTabs(props: { active: VideoTool; onChange: (tool: VideoTool) => void }) {
  useLocaleSubscription();
  const labels: Record<VideoTool, string> = { generate: "Generate", enhance: "Enhance", modify: "Modify", speak: "Lip sync", relight: "Relight", editor: "Clip editor" };
  const tools = [...VIDEO_TOOLS.filter(tool => tool.id !== "editor"), VIDEO_TOOLS.find(tool => tool.id === "editor")!];
  return <CreationToolTabs {...props} label={localeText("interface.video_tools_a7132c6f", { defaultValue: "Video tools" })} tools={tools.map(tool => ({ ...tool, short: labels[tool.id], studio: tool.id === "editor" }))} />;
}

export function AudioToolTabs(props: { active: AudioTool; onChange: (tool: AudioTool) => void }) {
  const { t } = useTranslation();
  const labels: Record<AudioTool, string> = {
    voice_design: t("audio.composer.tabs.design", { defaultValue: "Design voice" }),
    transcribe: t("audio.composer.tabs.transcribe", { defaultValue: "Transcribe" }),
    audio_generate: t("audio.composer.tabs.generate", { defaultValue: "Generate" }),
    text_to_speech: t("audio.composer.tabs.voice", { defaultValue: "Voice" }),
    music_generate: t("audio.composer.tabs.music", { defaultValue: "Music" }),
    sound_generate: t("audio.composer.tabs.sound", { defaultValue: "Sound effects" }),
    voice_manage: t("audio.composer.tabs.voices", { defaultValue: "My voices" }),
    voice_change: t("audio.composer.tabs.change", { defaultValue: "Change voice" }),
    voice_isolate: t("audio.composer.tabs.isolate", { defaultValue: "Isolate voice" }),
  };
  return <CreationToolTabs {...props} label={t("audio.composer.tabs.label", { defaultValue: "Audio tools" })}
    tools={AUDIO_TOOLS.map(tool => ({ ...tool, short: labels[tool.id] }))} />;
}

export function ThreeDToolTabs({ text, onChange }: { text: boolean; onChange: (tool: "image_to_mesh" | "text_to_mesh") => void }) {
  useLocaleSubscription();
  return <CreationToolTabs active={text ? "text_to_mesh" : "image_to_mesh"} onChange={onChange} label={localeText("interface.3d_tools_9c6cb838", { defaultValue: "3D tools" })} tools={[
    { id: "image_to_mesh", short: "Image to 3D", get description() { return localeText("interface.build_a_model_from_object_views_37762985", { defaultValue: "Build a model from object views." }); } },
    { id: "text_to_mesh", short: "Text to 3D", get description() { return localeText("interface.describe_the_object_you_want_to_create_4b8d3862", { defaultValue: "Describe the object you want to create." }); } },
  ]} />;
}

function CreationToolTabs<T extends string>({ active, onChange, label, tools }: {
  active: T; onChange: (tool: T) => void; label: string;
  tools: readonly { id: T; short: string; description: string; studio?: boolean }[];
}) {
  const ref = useRef<HTMLElement>(null);
  // Fade whichever edge hides tabs, and keep the current tab in view.
  useEffect(() => {
    const nav = ref.current;
    if (!nav) return;
    const update = () => {
      nav.dataset.overflowStart = String(nav.scrollLeft > 1);
      nav.dataset.overflowEnd = String(nav.scrollLeft + nav.clientWidth < nav.scrollWidth - 1);
    };
    const revealCurrent = () => {
      const current = nav.querySelector<HTMLElement>('[aria-current="page"]');
      if (!current) return;
      // Scroll only this row, leaving the feed and document in place.
      const frame = nav.getBoundingClientRect();
      const tab = current.getBoundingClientRect();
      const offset = tab.left < frame.left + 40 ? tab.left - frame.left - 40
        : tab.right > frame.right - 40 ? tab.right - frame.right + 40 : 0;
      if (offset) nav.scrollBy({ left: offset, behavior: "instant" });
    };
    revealCurrent();
    update();
    nav.addEventListener("scroll", update, { passive: true });
    const observer = typeof ResizeObserver === "undefined" ? undefined : new ResizeObserver(() => { revealCurrent(); update(); });
    observer?.observe(nav);
    return () => {
      nav.removeEventListener("scroll", update);
      observer?.disconnect();
    };
  }, [active]);
  return <nav ref={ref} aria-label={label} className="studio-tool-tabs">
    {tools.map((tool, index) => <button key={tool.id} type="button" className="studio-tool-tab"
      data-studio-start={tool.studio && !tools[index - 1]?.studio || undefined}
      aria-current={active === tool.id ? "page" : undefined} title={tool.description}
      onClick={() => { if (active !== tool.id) onChange(tool.id); }}>
      {tool.short}
    </button>)}
  </nav>;
}

/** Scrollable media navigation and tool selection shared by the media composers. */
export function CreationWorkspaceHeader({
  active,
  title,
  headingLevel = "h1",
  toolSelection = true,
  videoMode = "generate",
  onMediaChange,
  imageTool = "generate",
  onImageToolChange,
  audioTool = "text_to_speech",
  onAudioToolChange,
  videoTool = "generate",
  onVideoToolChange,
  onToolsBack,
  pickerOpen,
  toolIcon,
  actions,
}: {
  toolSelection?: boolean;
  toolIcon?: ReactNode;
  actions?: ReactNode;
  onToolsBack?: () => void;
  pickerOpen?: boolean;
  imageTool?: ImageTool;
  onImageToolChange?: (tool: ImageTool) => void;
  audioTool?: AudioTool;
  onAudioToolChange?: (tool: AudioTool) => void;
  videoTool?: VideoTool;
  onVideoToolChange?: (tool: VideoTool) => void;
  active: "image" | "video" | "audio" | "3d";
  title: string;
  headingLevel?: "h1" | "h2";
  videoMode?: string;
  onMediaChange?: (media: "image" | "video" | "audio") => void;
}) {
  const { t } = useTranslation();
  const Heading = headingLevel;
  const [toolsOpen, setToolsOpen] = useState(false);
  const toolsID = useId();
  const toolsTriggerRef = useRef<HTMLButtonElement>(null);
  const workspace = useCreationWorkspaceLayout();

  const open = pickerOpen ?? toolsOpen;
  const setOpen = (next: boolean) => {
    if (onToolsBack) { if (next !== open) onToolsBack(); }
    else setToolsOpen(next);
  };
  const imagePicker = active === "image" && onImageToolChange;
  const videoPicker = active === "video" && onVideoToolChange;
  const audioPicker = active === "audio" && onAudioToolChange;
  const hasWorkspacePicker = imagePicker || videoPicker || audioPicker;
  const trigger = (toggle: () => void, expanded: boolean) => <Button ref={toolsTriggerRef} variant="secondary" aria-label={t("generate.tools", { defaultValue: "Tools" })}
    aria-expanded={onToolsBack && pickerOpen === undefined ? undefined : expanded}
    aria-controls={onToolsBack && pickerOpen === undefined ? undefined : toolsID}
    onClick={toggle} className="studio-tools-trigger">
    <span className="studio-tools-trigger-copy">
    {active === "image" ? <span className="studio-tools-trigger-label">{t("generate.tools", { defaultValue: "Tools" })}</span> : null}
    <span className="studio-tools-trigger-title">
      {toolIcon ?? (active === "3d" ? <ThreeDIcon /> : <CreationToolIcon media={active} tool={active === "image" ? imageTool : active === "video" ? (onVideoToolChange ? videoTool : videoMode) : audioTool} />)}
      <span>{title}</span>
    </span>
    </span>
    <CaretDownIcon aria-hidden="true" className={cn("size-4 shrink-0 transition-transform duration-150 motion-reduce:transition-none", expanded && "rotate-180")} />
  </Button>;

  return (
    <div data-workspace-header data-media={active} className={active === "image" ? "mb-4" : "mb-3"} onKeyDown={event => {
      if (!hasWorkspacePicker || !open || event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
      toolsTriggerRef.current?.focus();
    }}>
      {!workspace ? <CreationMediaNav active={active} onMediaChange={onMediaChange} /> : null}
      <div className={actions && !open ? "flex items-center gap-2" : undefined}>
      <Heading className={`${active === "image" ? "mt-4" : "mt-3"} min-w-0 flex-1`} aria-label={title}>
        {toolSelection && hasWorkspacePicker ? trigger(() => setOpen(!open), open) : <span className="studio-tools-trigger-title px-1 py-2">{toolIcon}<span>{title}</span></span>}
      </Heading>
      {actions && !open ? <div className="mt-3 shrink-0">{actions}</div> : null}
      </div>
      {toolSelection && hasWorkspacePicker && open ? <div className="mt-2 pb-3">
        {imagePicker ? <WorkspaceToolPicker id={toolsID} media="image" tools={IMAGE_TOOLS} selected={imageTool}
          onSelect={tool => { setOpen(false); onImageToolChange(tool); toolsTriggerRef.current?.focus(); }} />
          : videoPicker ? <WorkspaceToolPicker id={toolsID} media="video" tools={VIDEO_TOOLS} selected={videoTool}
            onSelect={tool => { setOpen(false); onVideoToolChange(tool); toolsTriggerRef.current?.focus(); }} />
          : audioPicker ? <WorkspaceToolPicker id={toolsID} media="audio" tools={AUDIO_TOOLS} selected={audioTool}
            onSelect={tool => { setOpen(false); onAudioToolChange(tool); toolsTriggerRef.current?.focus(); }} /> : null}
      </div> : null}

    </div>
  );
}

function WorkspaceToolPicker<T extends string>({ id, media, tools, selected, onSelect }: {
  id: string;
  media: "image" | "video" | "audio";
  tools: readonly { id: T; name: string; description: string }[];
  selected: string;
  onSelect: (tool: T) => void;
}) {
  useLocaleSubscription();
  return <nav id={id} aria-label={media === "image" ? localeText("interface.image_tools_9b470a67", { defaultValue: "Image tools" }) : media === "audio" ? localeText("interface.audio_tools_b18c3477", { defaultValue: "Audio tools" }) : localeText("interface.video_tools_a7132c6f", { defaultValue: "Video tools" })} className="studio-workspace-tool-picker grid min-h-0 gap-1">
    {tools.map(tool => <ActionCard variant="secondary" selected={selected === tool.id} key={tool.id} aria-current={selected === tool.id ? "page" : undefined}
      onClick={() => onSelect(tool.id)} className="flex min-h-12 items-center gap-2.5 px-3 py-2">
      <CreationToolIcon media={media} tool={tool.id} className="size-[18px] shrink-0" />
      <span className="min-w-0 flex-1"><span className="block text-[13px] font-semibold">{tool.name}</span><span className="block text-[11.5px] text-ink-muted">{tool.description}</span></span>
      {selected === tool.id ? <CheckIcon aria-hidden="true" className="size-4 shrink-0 text-ink" /> : null}
    </ActionCard>)}
  </nav>;
}

export function CreationMediaNav({ active, onMediaChange, onThreeDChange }: {
  onThreeDChange?: () => void;
  active: WorkspaceCreationMedia;
  onMediaChange?: (media: "image" | "video" | "audio") => void;
}) {
  const { t } = useTranslation();
  const mediaNavRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const nav = mediaNavRef.current;
    const current = nav?.querySelector<HTMLElement>('[aria-current="page"]');
    if (!nav || !current) return;
    const revealCurrent = () => {
      const frame = nav.getBoundingClientRect();
      const link = current.getBoundingClientRect();
      if (!frame.width || !link.width) return;
      // Scroll only this row, leaving the composer and document in place.
      const offset = link.left < frame.left + 4 ? link.left - frame.left - 4
        : link.right > frame.right - 4 ? link.right - frame.right + 4 : 0;
      if (offset) nav.scrollBy({ left: offset, behavior: "instant" });
    };
    revealCurrent();
    if (typeof ResizeObserver === "undefined") return;
    // Composers stay mounted while hidden. Recheck when their row reappears
    // or changes width, without interrupting manual scrolling between layouts.
    const observer = new ResizeObserver(revealCurrent);
    observer.observe(nav);
    observer.observe(current);
    return () => observer.disconnect();
  }, [active]);

  return (
      <nav
        ref={mediaNavRef}
        aria-label={t("sidebar.create")}
        className="studio-media-nav"
      >
        <MediaLink
          to="/generate/image"
          label={t("projectShell.rooms.image.label")}
          active={active === "image"}
          onMediaChange={onMediaChange}
        />
        <MediaLink
          to="/generate/video"
          label={t("projectShell.rooms.video.label")}
          active={active === "video"}
          onMediaChange={onMediaChange}
        />
        <MediaLink
          to="/generate/audio"
          label={t("foundation.common.audio")}
          active={active === "audio"}
          onMediaChange={onMediaChange}
        />
        <Link to="/generate/$media" params={{ media: "3d" }} resetScroll={false}
          preload="intent" aria-current={active === "3d" ? "page" : undefined}
          data-studio-media="3d"
          onClick={(event) => {
            if (!onThreeDChange || shouldUseLinkNavigation(event)) return;
            event.preventDefault();
            onThreeDChange();
          }}
          className={cn(buttonVariants({ variant: "ghost", size: "sm" }), "studio-media-link", active === "3d" && "bg-[var(--selection-bg)] text-ink")}>
          <ThreeDIcon /><span>3D</span>
        </Link>
      </nav>
  );
}

function MediaLink({
  to,
  label,
  active,
  onMediaChange,
}: {
  to: "/generate/image" | "/generate/video" | "/generate/audio";
  label: string;
  active: boolean;
  onMediaChange?: (media: "image" | "video" | "audio") => void;
}) {
  const media = to.slice("/generate/".length) as
    | "image"
    | "video"
    | "audio";
  return (
    <Link
      to="/generate/$media"
      params={{ media }}
      preload="intent"
      resetScroll={false}
      aria-current={active ? "page" : undefined}
      data-studio-media={media}
      onClick={(event) => {
        if (!onMediaChange || shouldUseLinkNavigation(event)) return;
        event.preventDefault();
        onMediaChange(media);
      }}
      data-variant="ghost"
      data-finish="satin"
      className={cn(buttonVariants({ variant: "ghost", size: "sm" }), "studio-media-link", active && "bg-[var(--selection-bg)] text-ink")}
    >
      {media === "image" ? <ImageIcon aria-hidden="true" /> : media === "video" ? <VideoCameraIcon aria-hidden="true" /> : <WaveformIcon aria-hidden="true" />}
      <span className="min-w-0 truncate">{label}</span>
    </Link>
  );
}

function shouldUseLinkNavigation(event: MouseEvent<HTMLAnchorElement>) {
  return (
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey ||
    event.currentTarget.target === "_blank"
  );
}
